<template>
  <article class="mx-auto max-w-3xl bg-white border border-gray-200 rounded-md shadow-sm px-10 py-8 text-gray-700">
    <header class="pb-3 mb-6 border-b-2 border-orange-600">
      <h2 class="text-lg font-bold text-gray-900">{{ title }}</h2>
      <div v-if="identity.length" class="mt-1 flex flex-wrap gap-x-3 text-xs text-gray-700">
        <span v-for="part in identity" :key="part.label">
          <span v-if="part.label" class="mr-1 text-gray-500">{{ part.label }}</span><span class="sensitive">{{ part.value }}</span>
        </span>
      </div>
      <div v-if="dateLine || editedAt" class="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-gray-400">
        <span v-if="dateLine">{{ dateLine }}</span>
        <span v-if="editedAt" class="rounded-full bg-primary-50 px-2 py-0.5 font-medium text-primary-700">
          Edited {{ editedAt }}<template v-if="editedBy"> by <span class="sensitive">{{ editedBy }}</span></template>
        </span>
      </div>
    </header>

    <section v-for="section in visibleSections" :key="section.key" class="mb-6">
      <h3
        v-if="section.label"
        class="mb-3 pb-1 border-b border-gray-300 text-[11px] font-bold uppercase tracking-wider text-gray-500"
      >
        {{ section.label }}
      </h3>

      <dl
        v-if="section.style === 'key_value'"
        class="grid grid-cols-[minmax(0,11rem)_1fr] gap-x-4 gap-y-1.5 rounded bg-gray-50 px-4 py-3 text-xs"
      >
        <template v-for="[key, value] in keyValueEntries(content[section.key], section)" :key="key">
          <dt class="text-right font-semibold text-gray-500">{{ humanize(key) }}</dt>
          <DemoText tag="dd" class="text-gray-900 whitespace-pre-line" :text="value" :record="patientMbi" />
        </template>
      </dl>

      <DemoText
        v-else-if="section.style === 'sourced_text'"
        tag="p"
        class="text-sm leading-relaxed whitespace-pre-line"
        :text="textOf(content[section.key])"
        :record="patientMbi"
      />

      <ul v-else-if="isList(section)" class="space-y-2.5">
        <li v-for="(item, i) in content[section.key]" :key="i" class="relative pl-4">
          <span class="absolute left-0 top-0 font-bold text-orange-600">&bull;</span>
          <DemoText
            tag="div"
            class="text-sm text-gray-900 whitespace-pre-line"
            :text="typeof item === 'string' ? item : primaryText(item)"
            :record="patientMbi"
          />
          <DemoText
            v-for="[key, value] in listItemDetails(item, section)"
            :key="key"
            tag="div"
            class="mt-0.5 pl-1 text-xs text-gray-500 whitespace-pre-line"
            :text="`${humanize(key)}: ${value}`"
            :record="patientMbi"
          />
        </li>
      </ul>

      <div v-else-if="section.style === 'issue_action_list'" class="space-y-3">
        <div v-for="(item, i) in content[section.key]" :key="i">
          <DemoText tag="div" class="text-sm font-semibold text-gray-900 whitespace-pre-line" :text="item.issue" :record="patientMbi" />
          <div v-if="item.recommendation" class="relative mt-0.5 pl-4 text-sm whitespace-pre-line">
            <span class="absolute left-0 top-0 text-[10px] leading-5 text-orange-600">&#9654;</span>
            <DemoText :text="item.recommendation" :record="patientMbi" />
          </div>
        </div>
      </div>

      <table v-else-if="section.style === 'table'" class="w-full text-xs">
        <thead>
          <tr class="bg-gray-50 border-b border-gray-300">
            <th
              v-for="col in tableColumns(section, content[section.key])"
              :key="col"
              class="px-2 py-1.5 text-left text-[10px] font-bold uppercase tracking-wide text-gray-500"
            >
              {{ humanize(col) }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in content[section.key]" :key="i" class="border-b border-gray-100 even:bg-gray-50/50">
            <td
              v-for="(col, ci) in tableColumns(section, content[section.key])"
              :key="col"
              class="px-2 py-1.5 align-top sensitive"
              :class="ci === 0 ? 'font-semibold text-gray-900' : 'text-gray-700'"
            >
              {{ row[col] }}
            </td>
          </tr>
        </tbody>
      </table>

      <div v-else-if="section.style === 'accuracy_badge'">
        <span class="inline-block rounded bg-gray-50 px-2 py-0.5 text-xs font-semibold text-gray-900">
          Reconciliation accuracy: <span class="sensitive">{{ content[section.key].level || content[section.key] }}</span>
        </span>
        <DemoText
          v-if="content[section.key].explanation"
          tag="p"
          class="mt-1 text-xs text-gray-500"
          :text="content[section.key].explanation"
          :record="patientMbi"
        />
      </div>

      <DemoText
        v-else
        tag="p"
        class="text-sm leading-relaxed whitespace-pre-line"
        :text="typeof content[section.key] === 'object' ? keyValueEntries(content[section.key]).map(e => e.join(': ')).join('\n') : content[section.key]"
        :record="patientMbi"
      />
    </section>
  </article>
</template>

<script>
// @ts-check
import {
  humanize,
  isBlank,
  keyValueEntries,
  listItemDetails,
  primaryText,
  tableColumns,
} from '@/utils/clinicalDocumentFormat';
import DemoText from '@/components/DemoMode/DemoText.vue';

const LIST_STYLES = ['sourced_list', 'assessment_list'];
const ARRAY_STYLES = [...LIST_STYLES, 'issue_action_list', 'table'];

export default {
  components: { DemoText },
  props: {
    title: { type: String, required: true },
    sections: { type: Array, default: () => [] },
    content: { type: Object, default: () => ({}) },
    patientName: { type: String, default: '' },
    patientMbi: { type: String, default: '' },
    patientDob: { type: String, default: '' },
    engagementDate: { type: String, default: '' },
    generated: { type: String, default: '' },
    editedAt: { type: String, default: '' },
    editedBy: { type: String, default: '' },
  },
  computed: {
    identity() {
      return [
        { label: '', value: this.patientName },
        { label: 'MBI', value: this.patientMbi },
        { label: 'DOB', value: this.patientDob },
      ].filter(part => part.value);
    },
    dateLine() {
      return [
        this.engagementDate && `Engagement ${this.engagementDate}`,
        this.generated && `Generated ${this.generated}`,
      ].filter(Boolean).join('  ·  ');
    },
    visibleSections() {
      return this.sections.filter(section => {
        const data = this.content[section.key];
        if (isBlank(data)) return false;
        if (ARRAY_STYLES.includes(section.style)) return Array.isArray(data);
        if (section.style === 'key_value') {
          return typeof data === 'object' && !Array.isArray(data) && keyValueEntries(data, section).length > 0;
        }
        return true;
      });
    },
  },
  methods: {
    humanize,
    keyValueEntries,
    listItemDetails,
    primaryText,
    tableColumns,
    isList(section) {
      return LIST_STYLES.includes(section.style);
    },
    textOf(value) {
      return value && typeof value === 'object' ? value.text : value;
    },
  },
};
</script>
